import React, { useState, useEffect } from 'react';
import { Brain, RefreshCw, CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react';

/**
 * Context Health Indicator
 *
 * Shows current token usage as a pie chart with health status
 * Allows manual context compaction with confirmation
 */
const ContextHealthIndicator = ({ conversationId, onCompactionComplete }) => {
  const [contextHealth, setContextHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compacting, setCompacting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState(null);

  // Fetch context health status
  const fetchContextHealth = async () => {
    if (!conversationId || conversationId.startsWith('temp-')) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const response = await fetch(`/api/memory/context-check/${conversationId}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch context health');
      }

      const data = await response.json();
      setContextHealth(data);
    } catch (err) {
      console.error('Error fetching context health:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Trigger manual compaction
  const handleManualCompaction = async () => {
    setCompacting(true);
    setShowConfirmation(false);

    try {
      const response = await fetch(`/api/memory/compact/${conversationId}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Compaction failed');
      }

      const result = await response.json();

      // Refresh health status
      await fetchContextHealth();

      // Notify parent component
      if (onCompactionComplete) {
        onCompactionComplete(result);
      }

    } catch (err) {
      console.error('Error during manual compaction:', err);
      setError(err.message);
    } finally {
      setCompacting(false);
    }
  };

  useEffect(() => {
    fetchContextHealth();
  }, [conversationId]);

  // Don't show for temp/new conversations
  if (!conversationId || conversationId.startsWith('temp-')) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500">
        <Brain className="w-4 h-4 animate-pulse" />
        <span>Loading...</span>
      </div>
    );
  }

  if (error || !contextHealth) {
    return null; // Silent fail
  }

  const { contextCheck, recommendation } = contextHealth;
  const percentage = parseFloat(contextCheck.percentUsed);
  const isHealthy = percentage < 75;
  const isWarning = percentage >= 75 && percentage < 90;
  const isDanger = percentage >= 90;

  // Color based on health
  const getHealthColor = () => {
    if (isDanger) return 'text-red-600';
    if (isWarning) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getHealthIcon = () => {
    if (isDanger) return <AlertCircle className="w-4 h-4" />;
    if (isWarning) return <AlertTriangle className="w-4 h-4" />;
    return <CheckCircle className="w-4 h-4" />;
  };

  const getBgColor = () => {
    if (isDanger) return 'bg-red-50 border-red-200';
    if (isWarning) return 'bg-yellow-50 border-yellow-200';
    return 'bg-green-50 border-green-200';
  };

  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${getBgColor()}`}>
      {/* Pie Chart Icon */}
      <div className="relative flex items-center justify-center">
        <svg width="32" height="32" viewBox="0 0 32 32" className="transform -rotate-90">
          {/* Background circle */}
          <circle
            cx="16"
            cy="16"
            r="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            className="text-gray-200"
          />
          {/* Progress arc */}
          <circle
            cx="16"
            cy="16"
            r="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeDasharray={`${(percentage / 100) * 87.96} 87.96`}
            className={getHealthColor()}
          />
        </svg>
        {/* Percentage in center */}
        <span className={`absolute text-xs font-semibold ${getHealthColor()}`}>
          {Math.round(percentage)}%
        </span>
      </div>

      {/* Health Info */}
      <div className="flex-1">
        <div className={`flex items-center gap-1 text-xs font-medium ${getHealthColor()}`}>
          {getHealthIcon()}
          <span>{isHealthy ? 'Healthy' : isWarning ? 'Consider Compaction' : 'Compaction Recommended'}</span>
        </div>
        <div className="text-xs text-gray-600">
          {contextCheck.tokenCount.toLocaleString()} / {contextCheck.tokenLimit.toLocaleString()} tokens
        </div>
      </div>

      {/* Manual Compaction Button */}
      {(isWarning || isDanger) && (
        <div className="relative">
          {!showConfirmation ? (
            <button
              onClick={() => setShowConfirmation(true)}
              disabled={compacting}
              className={`flex items-center gap-1 px-3 py-1 text-xs font-medium text-white rounded transition-colors ${
                compacting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : isDanger
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-yellow-600 hover:bg-yellow-700'
              }`}
              title="Manually optimize context"
            >
              {compacting ? (
                <>
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  <span>Optimizing...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-3 h-3" />
                  <span>Optimize</span>
                </>
              )}
            </button>
          ) : (
            <div className="absolute right-0 top-0 z-10 bg-white border border-gray-300 rounded-lg shadow-lg p-3 min-w-[280px]">
              <p className="text-sm font-medium text-gray-900 mb-2">
                Optimize Context Now?
              </p>
              <p className="text-xs text-gray-600 mb-3">
                This will summarize older messages while preserving key information and recent context.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleManualCompaction}
                  className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                >
                  Yes, Optimize
                </button>
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Automatic Notice */}
      {isHealthy && (
        <div className="text-xs text-gray-500 italic">
          Auto-optimizes at 75%
        </div>
      )}
    </div>
  );
};

export default ContextHealthIndicator;
